import { useForm } from "@inertiajs/react"
import { useState } from "react"

import { DEFAULT_BRAND_COLOR } from "@/components/business-profile/brand-color-field"
import {
  DEFAULT_HOURS,
  type DayHours,
  HoursEditor,
  type HoursStatus,
  hoursSummary,
} from "@/components/business-profile/hours-editor"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import { type BusinessProfilePayload } from "./types"

// IMP-457: the profile-confirmation beat — "Is this you?" A row card of what
// the assistant learned; tap a row to fix it inline. "Looks good" saves and
// moves on; "Go back" (self-serve only) returns to business search.
// IMP-458: includes the brand color the scrape detected from the website —
// editable before confirming, with a sensible default when nothing was found.

type FieldKey =
  | "business_name"
  | "website"
  | "business_category"
  | "phone"
  | "address"
  | "services"
  | "description"

// IMP-527: hours edit inline like every other row; "hours" joins the editing
// state without being a plain text field.
type EditingKey = FieldKey | "hours"

const ROWS: { key: FieldKey; label: string; multiline?: boolean }[] = [
  { key: "business_name", label: "Name" },
  { key: "website", label: "Website" },
  { key: "business_category", label: "Category" },
  { key: "phone", label: "Phone" },
  { key: "address", label: "Address" },
  { key: "services", label: "Services", multiline: true },
  { key: "description", label: "About", multiline: true },
]

export function StepProfile({
  profile,
  industryOptions,
  truncated,
  onBack,
}: {
  profile: BusinessProfilePayload
  industryOptions: string[]
  truncated: boolean
  onBack: () => void
}) {
  const [editing, setEditing] = useState<EditingKey | null>(null)

  const { data, setData, patch, processing } = useForm({
    business_profile: {
      business_name: profile.business_name ?? "",
      website: profile.website ?? "",
      business_category: profile.business_category ?? "",
      phone: profile.phone ?? "",
      address: profile.address ?? "",
      services: profile.services ?? "",
      description: profile.description ?? "",
      brand_color: profile.brand_color ?? DEFAULT_BRAND_COLOR,
      // IMP-527: hours ride the same confirmation save as the other fields.
      hours_status: profile.hours_status ?? "open_no_hours",
      hours: profile.hours ?? DEFAULT_HOURS,
    },
  })

  function updateField(key: FieldKey, value: string) {
    setData("business_profile", { ...data.business_profile, [key]: value })
  }

  function handleSubmit() {
    patch("/onboarding/details")
  }

  const hoursLine = hoursSummary(
    data.business_profile.hours_status,
    data.business_profile.hours,
  )

  function updateHours(patch: {
    hours_status?: HoursStatus
    hours?: DayHours[]
  }) {
    setData("business_profile", { ...data.business_profile, ...patch })
  }

  // No website means no scan — there's nothing to confirm, so the step asks
  // instead of showing off (Chris's IMP-457 feedback).
  const scanned = Boolean(profile.website)

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 space-y-6 duration-500">
      <div className="space-y-2 text-center">
        <h1 className="text-xl font-semibold">
          {scanned ? "Is this you?" : "Tell me about your business"}
        </h1>
        <p className="text-muted-foreground text-sm">
          {scanned
            ? "Make sure I’ve got this right. Tap anything to fix it."
            : "Fill in what you can, you can always change it later."}
        </p>
      </div>

      <div className="bg-background overflow-hidden rounded-xl border">
        <div className="divide-border/70 divide-y">
          {ROWS.map((row) => {
            const value = data.business_profile[row.key]
            const isEditing = editing === row.key
            return (
              <div
                key={row.key}
                role="button"
                tabIndex={0}
                onClick={() => setEditing(row.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isEditing) setEditing(row.key)
                }}
                className="hover:bg-muted/40 flex w-full cursor-pointer gap-4 px-4 py-3 text-left transition-colors"
              >
                <span className="text-muted-foreground w-24 shrink-0 pt-0.5 text-sm">
                  {row.label}
                </span>
                {isEditing ? (
                  row.multiline ? (
                    <Textarea
                      value={value}
                      autoFocus
                      rows={3}
                      onChange={(e) => updateField(row.key, e.target.value)}
                      onBlur={() => setEditing(null)}
                      className="min-w-0 flex-1 text-sm"
                    />
                  ) : (
                    <Input
                      value={value}
                      autoFocus
                      list={
                        row.key === "business_category"
                          ? "onboarding-industries"
                          : undefined
                      }
                      onChange={(e) => updateField(row.key, e.target.value)}
                      onBlur={() => setEditing(null)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") setEditing(null)
                      }}
                      className="h-8 min-w-0 flex-1 text-sm"
                    />
                  )
                ) : (
                  <span className="min-w-0 flex-1 text-sm whitespace-pre-line">
                    {value || (
                      <span className="text-muted-foreground">Tap to add…</span>
                    )}
                  </span>
                )}
              </div>
            )
          })}

          {/* IMP-527: hours edit in place like every other row. The shared
              editor expands beneath the summary while open — status first,
              the weekly grid only for "Open with main hours" — and collapses
              behind the one-line summary on Done. */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => setEditing("hours")}
            onKeyDown={(e) => {
              if (e.key === "Enter" && editing !== "hours") setEditing("hours")
            }}
            className={cn(
              "flex w-full flex-col gap-3 px-4 py-3 text-left transition-colors",
              editing !== "hours" && "hover:bg-muted/40 cursor-pointer",
            )}
          >
            <div className="flex w-full gap-4">
              <span className="text-muted-foreground w-24 shrink-0 pt-0.5 text-sm">
                Hours
              </span>
              <span className="min-w-0 flex-1 text-sm">{hoursLine}</span>
              {editing === "hours" && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground -my-1 h-7 shrink-0"
                  onClick={(e) => {
                    e.stopPropagation()
                    setEditing(null)
                  }}
                >
                  Done
                </Button>
              )}
            </div>
            {editing === "hours" && (
              <HoursEditor
                status={data.business_profile.hours_status}
                hours={data.business_profile.hours}
                onStatusChange={(status) =>
                  updateHours({ hours_status: status })
                }
                onHourChange={(idx, patch) =>
                  updateHours({
                    hours: data.business_profile.hours.map((day, i) =>
                      i === idx ? { ...day, ...patch } : day,
                    ),
                  })
                }
              />
            )}
          </div>

          {/* IMP-458: the detected brand color — a native color picker keeps
              it editable before the client confirms. */}
          <label className="hover:bg-muted/40 flex w-full cursor-pointer gap-4 px-4 py-3 text-left transition-colors">
            <span className="text-muted-foreground w-24 shrink-0 text-sm">
              Brand color
            </span>
            <span className="flex min-w-0 flex-1 items-center gap-2 text-sm">
              <input
                type="color"
                value={data.business_profile.brand_color}
                onChange={(e) =>
                  setData("business_profile", {
                    ...data.business_profile,
                    brand_color: e.target.value,
                  })
                }
                aria-label="Brand color"
                className="border-border/60 size-4 shrink-0 cursor-pointer appearance-none rounded-full border [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-none [&::-webkit-color-swatch-wrapper]:p-0"
                style={{ backgroundColor: data.business_profile.brand_color }}
              />
              {data.business_profile.brand_color.toUpperCase()}
            </span>
          </label>
        </div>
      </div>

      <datalist id="onboarding-industries">
        {industryOptions.map((label) => (
          <option key={label} value={label} />
        ))}
      </datalist>

      <div className="flex justify-center gap-3">
        {!truncated && (
          <Button size="lg" variant="outline" onClick={onBack}>
            Go back
          </Button>
        )}
        <Button size="lg" onClick={handleSubmit} loading={processing}>
          Looks good
        </Button>
      </div>
    </div>
  )
}
